import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Input, InputNumber, Spin, Switch } from 'antd';
import { MailOutlined, SaveOutlined, ExperimentOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { useNewGui } from '../../hooks/useNewGui';
import { composeEmailFrom, isValidEmailAddress, parseEmailFrom } from '../../utils/emailFrom';

export interface SmtpPublicConfig {
  host: string;
  port: string;
  secure: boolean;
  user: string;
  from: string;
  passwordConfigured: boolean;
  source: 'db' | 'env' | 'unset';
}

interface Props {
  isSuperadmin: boolean;
}

const SmtpSettingsTab: React.FC<Props> = ({ isSuperadmin }) => {
  const useNew = useNewGui();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [cfg, setCfg] = useState<SmtpPublicConfig | null>(null);
  const [host, setHost] = useState('');
  const [port, setPort] = useState(587);
  const [secure, setSecure] = useState(false);
  const [user, setUser] = useState('');
  const [fromName, setFromName] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [pass, setPass] = useState('');
  const [testTo, setTestTo] = useState('');

  const applyCfg = useCallback((data: SmtpPublicConfig) => {
    setCfg(data);
    setHost(data.host || '');
    setPort(Number(data.port) || 587);
    setSecure(!!data.secure);
    setUser(data.user || '');
    const parsed = parseEmailFrom(data.from || '');
    setFromName(parsed.displayName);
    setFromEmail(parsed.email || data.user || '');
    setPass('');
  }, []);

  const fetchSmtp = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get<SmtpPublicConfig>('/api/system-settings/smtp');
      applyCfg(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load SMTP settings');
    } finally {
      setLoading(false);
    }
  }, [applyCfg]);

  useEffect(() => {
    if (isSuperadmin) fetchSmtp();
    else setLoading(false);
  }, [isSuperadmin, fetchSmtp]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const email = fromEmail.trim() || user.trim();
      if (email && !isValidEmailAddress(email)) {
        setError('From email must be a valid address (e.g. test.user@example.com).');
        setSaving(false);
        return;
      }
      const from = composeEmailFrom(fromName, email);
      const body: Record<string, unknown> = {
        host,
        port,
        secure,
        user,
        from,
      };
      if (pass.trim()) body.pass = pass.trim();
      const { data } = await api.put<SmtpPublicConfig>('/api/system-settings/smtp', body);
      applyCfg(data);
      setSuccess('SMTP settings saved. Self-service and PMA mail will use these when EMAIL_HOST is set.');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save SMTP settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError(null);
    setSuccess(null);
    try {
      const { data } = await api.post<{ message: string }>('/api/system-settings/smtp/test', {
        to: testTo.trim() || undefined,
      });
      setSuccess(data.message);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'SMTP test failed');
    } finally {
      setTesting(false);
    }
  };

  if (!isSuperadmin) {
    return (
      <Alert
        type="info"
        showIcon
        message="SMTP configuration is superadmin-only."
        description="Ask a superadmin to configure outbound email under Configuration → SMTP."
      />
    );
  }

  if (loading) return <Spin tip="Loading SMTP settings..." />;

  const cardClass = useNew
    ? 'bg-surface border border-border-subtle rounded-lg p-5 shadow-sm'
    : 'bg-white rounded-lg shadow-sm p-6';
  const labelClass = useNew ? 'text-ink-primary' : undefined;
  const hintClass = useNew ? 'text-xs text-ink-secondary mt-1' : 'text-xs text-gray-400 mt-1';
  const previewFrom = composeEmailFrom(fromName, fromEmail.trim() || user.trim());

  return (
    <div className="space-y-4 max-w-2xl">
      {error && <Alert type="error" message={error} showIcon closable onClose={() => setError(null)} />}
      {success && <Alert type="success" message={success} showIcon closable onClose={() => setSuccess(null)} />}

      <div className={cardClass}>
        <div className="flex items-center gap-2 mb-1">
          <MailOutlined className={useNew ? 'text-accent' : 'text-blue-600'} />
          <h2 className={useNew ? 'text-base font-semibold text-ink-primary' : 'text-lg font-semibold text-gray-700'}>
            Outbound SMTP
          </h2>
        </div>
        <p className={useNew ? 'text-sm text-ink-secondary mb-4' : 'text-sm text-gray-400 mb-4'}>
          Used by InfraConsole notifications and the myws password-reset portal.
          {cfg?.source === 'env' && (
            <span className="block mt-1 text-amber-600">
              Currently falling back to server environment variables (no DB host set).
            </span>
          )}
          {cfg?.source === 'db' && (
            <span className="block mt-1">Active source: database (system_settings).</span>
          )}
        </p>

        <Form layout="vertical" disabled={!isSuperadmin}>
          <Form.Item label={<span className={labelClass}>SMTP host</span>} required>
            <Input
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="smtp.zoho.com"
              autoComplete="off"
            />
          </Form.Item>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Form.Item label={<span className={labelClass}>Port</span>}>
              <InputNumber
                className="w-full"
                min={1}
                max={65535}
                value={port}
                onChange={(v) => {
                  const p = Number(v) || 587;
                  setPort(p);
                  if (p === 465) setSecure(true);
                  if (p === 587) setSecure(false);
                }}
              />
            </Form.Item>
            <Form.Item label={<span className={labelClass}>TLS (secure)</span>}>
              <div className="flex items-center gap-2 h-8">
                <Switch checked={secure} onChange={setSecure} />
                <span className={hintClass}>{secure ? 'SMTPS (465)' : 'STARTTLS (typical 587)'}</span>
              </div>
            </Form.Item>
          </div>
          <Form.Item label={<span className={labelClass}>Username</span>}>
            <Input
              value={user}
              onChange={(e) => setUser(e.target.value)}
              placeholder="mailbox@example.com"
              autoComplete="off"
            />
          </Form.Item>
          <Form.Item
            label={<span className={labelClass}>Password</span>}
            extra={
              <span className={hintClass}>
                {cfg?.passwordConfigured
                  ? 'A password is stored. Leave blank to keep it unchanged.'
                  : 'No password stored yet.'}
              </span>
            }
          >
            <Input.Password
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              placeholder={cfg?.passwordConfigured ? '••••••••' : 'SMTP password'}
              autoComplete="new-password"
            />
          </Form.Item>
          <Form.Item
            label={<span className={labelClass}>From display name</span>}
            extra={<span className={hintClass}>Shown as the sender name in the inbox (optional).</span>}
          >
            <Input
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="e.g. Test User"
              autoComplete="off"
            />
          </Form.Item>
          <Form.Item
            label={<span className={labelClass}>From email</span>}
            extra={
              <span className={hintClass}>
                Combined From header example:{' '}
                <code>Test User &lt;test.user@example.com&gt;</code>
                {previewFrom ? (
                  <>
                    {' '}— preview: <code>{previewFrom}</code>
                  </>
                ) : null}
              </span>
            }
          >
            <Input
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="test.user@example.com"
              autoComplete="off"
            />
          </Form.Item>

          <div className="flex flex-wrap gap-2 mt-2">
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
              Save SMTP
            </Button>
          </div>
        </Form>
      </div>

      <div className={cardClass}>
        <h3 className={useNew ? 'text-sm font-semibold text-ink-primary mb-2' : 'text-base font-semibold text-gray-700 mb-2'}>
          Test connection
        </h3>
        <p className={hintClass + ' mb-3'}>
          Sends a short probe using the currently saved settings (save first if you just edited the form).
        </p>
        <div className="flex flex-wrap gap-2 items-start">
          <Input
            style={{ maxWidth: 320 }}
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="Optional recipient (defaults to your login email)"
          />
          <Button icon={<ExperimentOutlined />} loading={testing} onClick={handleTest}>
            Send test email
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SmtpSettingsTab;
